import type { ReactElement, ReactNode } from 'react';
import { Menu } from '@base-ui/react/menu';

import arrowStyles from '../shared/floating-arrow.module.css';
import { Icon, type IconName } from '../Icon';
import styles from './DropdownMenu.module.css';

/** 与 Ant Design Dropdown 的 placement 对齐（12 种）。 */
export type DropdownMenuPlacement =
  | 'topLeft' | 'top' | 'topRight'
  | 'bottomLeft' | 'bottom' | 'bottomRight'
  | 'leftTop' | 'left' | 'leftBottom'
  | 'rightTop' | 'right' | 'rightBottom';

/** 触发方式；contextMenu 与 Ant 对齐后另行支持。 */
export type DropdownMenuTriggerMode = 'click' | 'hover';

export interface DropdownMenuItem {
  /** 相邻同名项归入同一组；不传时保持原有平铺菜单。 */
  group?: string;
  href?: string;
  description?: string;
  leadingContent?: ReactNode;
  trailingIcon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  icon?: IconName;
  hoverIcon?: IconName;
  hoverIconTone?: 'danger';
  id: string;
  label: string;
  separatorBefore?: boolean;
}

export interface DropdownMenuProps {
  appearance?: 'compact' | 'account';
  ariaLabel: string;
  /** 是否显示指向触发器的箭头，默认显示。 */
  arrow?: boolean;
  disabled?: boolean;
  items: readonly DropdownMenuItem[];
  onItemSelect: (item: DropdownMenuItem) => void;
  onOpenChange?: (open: boolean) => void;
  placement?: DropdownMenuPlacement;
  trigger: ReactElement;
  triggerMode?: DropdownMenuTriggerMode;
}

const placementMap: Record<DropdownMenuPlacement, { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' }> = {
  bottom: { align: 'center', side: 'bottom' },
  bottomLeft: { align: 'start', side: 'bottom' },
  bottomRight: { align: 'end', side: 'bottom' },
  left: { align: 'center', side: 'left' },
  leftBottom: { align: 'end', side: 'left' },
  leftTop: { align: 'start', side: 'left' },
  right: { align: 'center', side: 'right' },
  rightBottom: { align: 'end', side: 'right' },
  rightTop: { align: 'start', side: 'right' },
  top: { align: 'center', side: 'top' },
  topLeft: { align: 'start', side: 'top' },
  topRight: { align: 'end', side: 'top' },
};

export function DropdownMenu({ appearance = 'compact', ariaLabel, arrow = true, disabled = false, items, onItemSelect, onOpenChange, placement = 'bottomRight', trigger, triggerMode = 'click' }: DropdownMenuProps) {
  const { align, side } = placementMap[placement];
  const hasGroups = items.some((item) => Boolean(item.group));
  const groups: { label?: string; items: DropdownMenuItem[] }[] = [];
  if (hasGroups) for (const item of items) {
    const previous = groups.at(-1);
    if (previous && previous.label === item.group) previous.items.push(item);
    else groups.push({ label: item.group, items: [item] });
  }

  function renderItem(item: DropdownMenuItem) {
    return <div key={item.id}>
      {item.separatorBefore ? <Menu.Separator className={styles.separator} /> : null}
      <Menu.Item render={item.href ? <a href={item.href} /> : undefined} className={styles.item} data-description={Boolean(item.description) || undefined} data-danger={item.danger || undefined} disabled={item.disabled} onClick={() => onItemSelect(item)}>
        {item.leadingContent}
        {item.icon ? <span className={styles.itemIcon}>
          <Icon className={item.hoverIcon ? styles.defaultIcon : undefined} name={item.icon} size={appearance === 'account' ? 'md' : 'sm'} />
          {item.hoverIcon ? <Icon className={styles.hoverIcon} data-tone={item.hoverIconTone} name={item.hoverIcon} size={appearance === 'account' ? 'md' : 'sm'} /> : null}
        </span> : null}
        <span className={styles.itemText}><span>{item.label}</span>{item.description ? <span className={styles.description}>{item.description}</span> : null}</span>
        {item.trailingIcon ? <Icon className={styles.trailingIcon} name={item.trailingIcon} size="sm" /> : null}
      </Menu.Item>
    </div>;
  }

  return (
    <Menu.Root disabled={disabled} onOpenChange={(open) => onOpenChange?.(open)}>
      {/*
       * `ariaLabel` 用作触发器的可访问名称：项目里的触发器通常是纯图标或笼统文字（如“操作”），
       * 需要上下文名称（如“高一物理…课程操作”）；菜单名由 Popup 的 aria-label 承担。
       */}
      <Menu.Trigger aria-label={ariaLabel} openOnHover={triggerMode === 'hover' || undefined} render={trigger} />
      <Menu.Portal>
        <Menu.Positioner align={align} className={styles.positioner} side={side} sideOffset={arrow ? 6 : 4}>
          <Menu.Popup aria-label={ariaLabel} className={styles.popup} data-appearance={appearance} data-arrow={arrow || undefined}>
            {arrow ? <Menu.Arrow className={[styles.arrow, arrowStyles.arrow].join(' ')} /> : null}
            {hasGroups ? <div className={styles.groupedContent}>{groups.map((group, index) => <div key={`${index}-${group.label ?? ''}`}>
              {index > 0 ? <Menu.Separator className={styles.separator} /> : null}
              {group.label ? <Menu.Group>
                <Menu.GroupLabel className={styles.groupLabel}>{group.label}</Menu.GroupLabel>
                {group.items.map(renderItem)}
              </Menu.Group> : group.items.map(renderItem)}
            </div>)}</div> : items.map(renderItem)}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
